<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <div class="mx-12 ps-10 mr-10 blocContent">
      <v-row class="mb-0">
        <v-icon>
          mdi-account
        </v-icon>
        <p
          class="mb-0 ml-5 titleStyle"
        >
          {{ $t('forms.labels.name') }}
        </p>
      </v-row>
      <v-row class="mt-0 ml-5">
        <v-col>
          <VTextField
            v-model="data.name"
            outlined
            :label="$t('forms.labels.name')"
            :rules="{ required: true, min: 1, max: 200 }"
            data-cy="serviceProviderInfo-name"
          />
        </v-col>
      </v-row>
    </div>

    <div class="mx-12 ps-10 mr-10 blocContent">
      <v-row class="mb-0">
        <v-icon>
          mdi-image
        </v-icon>
        <p
          class="ml-5 mb-0 titleStyle"
        >
          {{ $t('forms.labels.logo') }}
        </p>
      </v-row>
      <v-row class="mt-0">
        <v-col>
          <VFile
            v-model="data.logo"
            :label="$t('forms.labels.logo')"
            isEdition
            showSize
            cyData="serviceProviderInfo-selectLogo"
            outlined
          />
        </v-col>
      </v-row>
    </div>

    <div class="mx-12 ps-10 mr-10 blocContent">
      <v-row class="mb-4">
        <v-icon>
          mdi-map-marker
        </v-icon>
        <p
          class="ml-5 mb-0 titleStyle"
        >
          {{ $t('forms.labels.address') }}
        </p>
      </v-row>

      <v-row class="mt-0 ml-7 mr-0">
        <v-col class="modalColPadding">
          <AutocompleteAdresse
            :disabled="disableAutoComplete"
            @change="updateAddress"
          />
        </v-col>
      </v-row>
      <v-row class="mt-0 ml-7 mr-0">
        <v-col class="modalColPadding">
          <AddressFormPart
            :details="addressDetails"
            :adressWithAutocomplete="true"
            @change="onChangeAddress"
          />
        </v-col>
      </v-row>
    </div>


    <div class="mx-12 ps-10 blocContent">
      <v-row class="mb-0">
        <v-icon color="#fff">
          mdi-email
        </v-icon>
        <p
          class="mb-0 ml-5 titleStyle"
        >
          {{ $t('forms.labels.contact') }}
        </p>
      </v-row>
      <v-row class="mt-0 ml-5">
        <v-col>
          <VTextField
            v-model="data.email"
            :label="$t('forms.labels.email')"
            :rules="{ max: 200, email: true }"
            outlined
            data-cy="serviceProviderInfo-email"
          />
        </v-col>
      </v-row>
      <v-row class="ml-5">
        <v-col>
          <PhoneNumberInput
            :refObserver="$refs.observer"
            :value="data.phoneNumber.number"
            :disabled="!hasPermission('members.roles.edit')"
            :isPhoneNumberValid="data.isPhoneNumberValid"
            data-cy="serviceProviderInfo-phone"
            @inputChanged="phoneInput"
          />
          <v-col />
        </v-col>
      </v-row>
    </div>

    <div class="mx-12 ps-10 blocContent">
      <v-row class="mb-0">
        <v-icon>
          mdi-calendar
        </v-icon>
        <p class="mb-0 ml-5 titleStyle">
          {{ $t('forms.labels.dateFormat') }}
        </p>
      </v-row>
      <v-row class="ml-5">
        <v-col>
          <VSelect
            v-model="data.dateFormat"
            :label="$t('forms.labels.dateFormat')"
            itemValue="value"
            :validateAtInitialization="!serviceProvider.empty"
            :items="dateFormats"
            clearable
            outlined
            data-cy="serviceProviderDateFormat"
            :disabled="!isAdmin"
          />
        </v-col>
      </v-row>
    </div>

    <div class="mx-12 ps-10 blocContent">
      <v-row class="mb-0">
        <v-icon>
          mdi-web
        </v-icon>
        <p
          class="mb-0 ml-5 titleStyle"
        >
          {{ $t('forms.labels.lang') }}
        </p>
      </v-row>
      <v-row class="ml-5">
        <v-col cols="4">
          <VTextField
            label="Main"
            disabled
            outlined
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="data.lang"
            :label="$t('forms.labels.lang')"
            itemValue="key"
            :validateAtInitialization="!serviceProvider.empty"
            :items="langs"
            outlined
            :datacy="dataCy.serviceProviderInfo.form.mainLanguage"
          />
        </v-col>
      </v-row>
      <v-row class="ml-5">
        <v-col cols="4">
          <VTextField
            label="Optional"
            disabled
            outlined
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="data.optionalLangs"
            :label="$t('forms.labels.lang')"
            itemValue="key"
            :validateAtInitialization="!serviceProvider.empty"
            :items="optionalLangs"
            multiple
            outlined
            :datacy="dataCy.serviceProviderInfo.form.optionalLanguage"
          />
        </v-col>
      </v-row>
    </div>

    <div class="mx-12 ps-10 blocContent">
      <v-row class="mb-0">
        <v-icon>
          mdi-email
        </v-icon>
        <p
          class="mb-0 ml-5 titleStyle"
        >
          {{ $t('forms.labels.warehouseEmail') }}
        </p>
      </v-row>
      <v-row class="mt-0 ml-5">
        <v-col>
          <VTextField
            v-model="data.warehouseEmail"
            :label="$t('forms.labels.warehouseEmail')"
            outlined
            :rules="{ max: 200, email: true }"
            data-cy="serviceProviderInfo-warehouseEmail"
          />
        </v-col>
      </v-row>
    </div>
    <div class="mx-12 ps-10 blocContent">
      <v-row class="mb-0">
        <v-icon>
          mdi-web
        </v-icon>
        <p
          class="mb-0 ml-5 titleStyle"
        >
          {{ $t('forms.labels.website') }}
        </p>
      </v-row>
      <v-row class="mt-0 ml-5">
        <v-col>
          <VTextField
            v-model="data.website"
            :label="$t('forms.labels.website')"
            outlined
            :rules="{ max: 200 }"
            data-cy="serviceProviderInfo-webSite"
          />
        </v-col>
      </v-row>
    </div>
    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirtyChanged', dirty)"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('validChanged', valid)"
    />
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import { mapGetters, mapActions } from 'vuex';
import { LOCALES } from '@/locales/i18n';
import VFile from '@/components/shared/widgets/forms/VuetifyInputs/VFile';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import FormFilesMixin from '@/mixins/FormFilesMixin';
import PhoneNumberInput from '@/components/shared/widgets/forms/PhoneNumberInput.vue';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import { serviceProviderInfo } from '@/aq-data-cy.json';
import dateFormats from '@/constants/serviceProviderDateFormat';
import AutocompleteAdresse from '@/components/AutocompleteAdresse.vue';
import AddressFormPart from '@/components/shared/forms-controls/AddressFormPart.vue';
import { isEmpty } from 'lodash';


export default {
    components: {
        VFile,
        VSelect,
        VTextField,
        ValidationObserver,
        PhoneNumberInput,
        SlotScopeWatcher,
        AddressFormPart,
        AutocompleteAdresse,
    },
    mixins: [
        FormFilesMixin,
    ],
    props: {
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
        serviceProvider: {
            type: Object,
            default: () => ({ empty: true }),
        },
        isMyTeam: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            fileFields: ['logo'],
            dateFormats,
            data: {
                logo: null,
                name: null,
                email: null,
                address: null,
                city: null,
                province: null,
                postalCode: null,
                country: null,
                phoneNumber: { number: '' },
                lang: null,
                optionalLangs: [],
                website: null,
                warehouseEmail: null,
                isPhoneNumberValid: true,
                dateFormat: null,
            },
        };
    },
    computed: {
        ...mapGetters([
            'teamID',
            'hasPermission',
            'hasRole',
        ]),
        addressDetails() {
            return {
                address: this.data.address,
                city: this.data.city,
                country: this.data.country,
                postalCode: this.data.postalCode,
                province: this.data.province,
                streetNumber: this.data.streetNumber,
                appartmentNumber: this.data.appartmentNumber,
            };
        },
        langs() {
            return LOCALES.map(lang => ({
                name: this.$t(`langs.${lang}`),
                key: lang,
                disabled: !!this.data.optionalLangs?.includes(lang),
            }));
        },
        optionalLangs() {
            return LOCALES.map(lang => ({
                name: this.$t(`langs.${lang}`),
                key: lang,
                disabled: this.data.lang === lang,
            }));
        },
        dataCy() {
            return {
                serviceProviderInfo,
            };
        },
        isAdmin() {
            return ((this.hasRole('administrator')));
        },

    },
    watch: {
        serviceProvider: {
            async handler() {
                if (!this.serviceProvider.phoneNumber) {
                    this.serviceProvider.phoneNumber = { number: '' };
                }

                this.data = { ...this.data, ...(await this.prepareEditionData(this.serviceProvider)) };
            },
            immediate: true,
        },
        data: {
            handler() {
                this.updateData();
                this.activePreventQuitFormular();
            },
            deep: true,
        },
    },
    async created() {
        if (this.isMyTeam) {
            // User check just their own team from configuration menue
            await this.setMyServiceProvider();
            this.disablePreventQuitFormular();
        }
    },
    methods: {
        ...mapActions({
            disablePreventQuitFormular: 'disablePreventQuitFormular',
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),
        async setMyServiceProvider() {
            const { data: team } = await this.$rq.getTeam(this.teamID);
            if (!team.phoneNumber) {
                team.phoneNumber = { number: '' };
            }
            this.data = { ...this.data, ...(await this.prepareEditionData(team)) };
        },
        updateData() {
            this.$emit('sendData', this.data);
            if (this.dirty) this.activePreventQuitFormular();
            else this.disablePreventQuitFormular();
        },
        async onChangeAddress(details) {
            this.data = {
                ...this.data,
                ...details,
            };
            await this.updateData();
        },
        async updateAddress(adress) {
            if (!isEmpty(adress)) {
                this.data = {
                    ...this.data,
                    city: adress?.locality || adress?.sublocality_level_1 || adress?.administrative_area_level_3 || adress.administrative_area_level_2 || '',
                    country: adress?.country || '',
                    province: adress?.administrative_area_level_1 || '',
                    postalCode: adress?.postal_code || adress?.postal_code_prefix || '',
                    streetNumber: adress?.street_number || '',
                    address: adress?.route || '',
                };
                this.$emit('dirtyChange', true);
            }
            await this.updateData();
        },
        phoneInput(number, payload) {
            this.data.phoneNumber = !number ? {} : payload;
            this.data.isPhoneNumberValid = payload.valid;
            this.updateData();
        },
    },
};
</script>
<style scoped lang="scss">
  .titleStyle{
      font-size: 16px;
      font-weight: 500;
      color:#8D969F
    }

    @media screen and (max-width: 992px) {
      .blocContent {
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
    }
    @media screen and (max-width: 600px) {
      .blocContent {
        padding-left: 20px !important;
        padding-right: 20px !important;
      }
    }


</style>
